
import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Footer = () => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Newsletter signup - would integrate with backend service
    try {
      // This would call a newsletter service API
      // For now, just show user feedback
      const formData = new FormData(e.target as HTMLFormElement);
      const email = formData.get('email') as string;
      
      if (email && email.includes('@')) {
        // Success feedback would be handled here
        toast.success("Successfully subscribed to newsletter!");
      }
    } catch (error) {
      console.error("Newsletter signup failed:", error);
    }
  };

  return (
    <footer className="relative mt-12 sm:mt-16 md:mt-20 border-t border-solr-nebula-purple/10 pt-10 sm:pt-12 md:pt-16 pb-6 sm:pb-8">
      {/* Neural connection effects */}
      <div className="absolute top-0 left-1/4 w-0 neural-connection animate-neural-connection hidden md:block"></div>
      <div className="absolute top-0 left-3/4 w-0 neural-connection animate-neural-connection hidden md:block" style={{ animationDelay: "1s" }}></div>

      {/* Stars effect - hidden on mobile */}
      <div className="star w-1 h-1 top-10 left-[10%] animate-star-twinkle hidden sm:block"></div>
      <div className="star w-1.5 h-1.5 top-20 left-[30%] animate-star-twinkle hidden sm:block" style={{ animationDelay: "0.5s" }}></div>
      <div className="star w-1 h-1 top-15 left-[70%] animate-star-twinkle hidden sm:block" style={{ animationDelay: "1s" }}></div>
      <div className="star w-2 h-2 top-25 left-[85%] animate-star-twinkle hidden sm:block" style={{ animationDelay: "1.5s" }}></div>

      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 mb-8 sm:mb-10">
          <div className="sm:col-span-2">
            <h1 className="text-xl sm:text-2xl font-bold neural-text mb-3 sm:mb-4">SOLR</h1>
            <p className="text-sm sm:text-base text-solr-starfield-silver max-w-md mb-4 sm:mb-6">
              Premium nootropic beverages that balance neural stimulation with adaptogenic wellness. 
              Elevate your mind with the cosmic energy of SOLR.
            </p>
            <form onSubmit={handleSubmit} className="space-y-2">
              <h3 className="text-xs sm:text-sm font-medium">JOIN OUR CONSTELLATION</h3>
              <div className="flex flex-col sm:flex-row max-w-md gap-2 sm:gap-0">
                <Input 
                  type="email" 
                  name="email"
                  placeholder="Enter your email" 
                  className="rounded-full sm:rounded-l-full sm:rounded-r-none sm:border-r-0 bg-solr-space-dark/50 border-solr-nebula-purple/20 text-solr-starfield-white focus-visible:ring-solr-nebula-purple text-sm"
                  required
                />
                <Button type="submit" className="rounded-full sm:rounded-l-none sm:rounded-r-full bg-gradient-to-r from-solr-nebula-purple to-solr-electric-blue hover:shadow-lg hover:shadow-solr-nebula-purple/20 text-sm">
                  Subscribe
                </Button>
              </div>
              <p className="text-[10px] sm:text-xs text-solr-starfield-silver">
                By subscribing, you agree to our Privacy Policy and consent to receive our newsletters.
              </p>
            </form>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">NAVIGATION</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-solr-starfield-silver text-sm">
              <li>
                <Link to="/" className="hover:text-solr-nebula-purple transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/shop" className="hover:text-solr-nebula-purple transition-colors">Shop</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-solr-nebula-purple transition-colors">About</Link>
              </li>
              <li>
                <Link to="/ingredients" className="hover:text-solr-nebula-purple transition-colors">Ingredients</Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">SUPPORT</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-solr-starfield-silver text-sm">
              <li>
                <Link to="/faq" className="hover:text-solr-nebula-purple transition-colors">FAQ</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-solr-nebula-purple transition-colors">Contact Us</Link>
              </li>
              <li>
                <Link to="/shipping" className="hover:text-solr-nebula-purple transition-colors">Shipping</Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-solr-nebula-purple transition-colors">Terms & Conditions</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-solr-nebula-purple transition-colors">Privacy Policy</Link>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-between items-center pt-6 sm:pt-8 border-t border-solr-nebula-purple/10 gap-4">
          <p className="text-xs sm:text-sm text-solr-starfield-silver text-center sm:text-left">
            &copy; {new Date().getFullYear()} SOLR Drinks. All rights reserved.
          </p>
          <div className="flex space-x-3 sm:space-x-4">
            <a 
              href="https://instagram.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={18} className="sm:w-5 sm:h-5" />
            </a>
            <a 
              href="https://twitter.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors"
              aria-label="Twitter"
            >
              <Twitter size={18} className="sm:w-5 sm:h-5" />
            </a>
            <a 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors"
              aria-label="Facebook"
            >
              <Facebook size={18} className="sm:w-5 sm:h-5" />
            </a>
            <a 
              href="mailto:info@solrdrinks.com" 
              className="p-1.5 sm:p-2 text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors"
              aria-label="Email"
            >
              <Mail size={18} className="sm:w-5 sm:h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
